<script setup lang="ts">
/**
 * The page header band — MAR-45, reshaped by Sistema Portafolio v0.2 (MAR-127) and Frame-in-Frame (MAR-1337).
 *
 * **It is part of the sheet, and it is paper.** It spans the whole sheet, sits directly under the
 * sheet's top edge, and the context rail sits inside it, below the bar (v0.2 §07).
 *
 * **It owns `#marcus-appbar-slot`**, into which the open page teleports its breadcrumbs and its own
 * actions (see `useAppBar.ts`).
 *
 * When the left navigation sidebar is Docked (`isSidebarCollapsed === false`), the collapse toggle
 * button lives inside `IconRail.vue`'s brand header. When the sidebar is Collapsed (`0px` width,
 * including during Floating Peek), the sidebar toggle button (`PanelLeft`) mounts here at the
 * top-left of the Main Canvas Card (`v-if="isSidebarCollapsed"`): hovering it triggers Floating Peek
 * (`startSidebarPeek`) and clicking it docks the sidebar open (`dockSidebar`).
 *
 * The right-hand `ContextRail` toggle (`◨`) moved to the persistent Bottom Frame Bar in
 * `PanelLayout.vue` alongside the `▷ Agent` control (MAR-1337).
 *
 * **One deliberate exception (MAR-437 follow-up):** it DOES collapse to nothing while the chat's
 * document preview panel is open.
 */
import { PanelLeft, Plus } from 'lucide-vue-next'

const { t } = useI18n()
const webChat = useWebChatStore()
const { isSidebarCollapsed, isSidebarPeeking, dockSidebar, startSidebarPeek } = usePanelLayout()
const route = useRoute()

// The phone-only fallback title (MAR-1350): shown while no page has claimed the bar's slot.
const barClaimed = useAppBarClaimed()
const fallbackTitleKey = computed(() => mobileHeaderTitleKey(route.path))
const fallbackTitle = computed(() => (fallbackTitleKey.value ? t(fallbackTitleKey.value) : ''))

function startNewChat() {
  webChat.startNewChat()
  return navigateTo('/app')
}
</script>

<template>
  <header
    v-if="!webChat.previewDocument"
    class="h-(--header-h) shrink-0 flex items-stretch border-b border-border bg-background text-foreground relative z-30"
  >
    <!-- Top-left sidebar toggle button: only visible when the sidebar is collapsed (or peeking). -->
    <button
      v-if="isSidebarCollapsed"
      type="button"
      data-appbar-sidebar-toggle
      class="hidden sm:flex shrink-0 items-center justify-center h-9 w-9 ml-1.5 self-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
      :title="t('sidebar.expand')"
      :aria-label="t('sidebar.expand')"
      :aria-expanded="isSidebarPeeking"
      @mouseenter="startSidebarPeek"
      @click="dockSidebar"
    >
      <PanelLeft class="h-4 w-4" />
    </button>

    <!-- Phone only: the page's title when no page has supplied one (the chat home, an open
         conversation). A page that claims the bar names itself and this steps aside, so the two are
         never on screen together. Client-only because claims register on mount — rendering it on the
         server would flash a route-derived name over a page that is about to supply its own. -->
    <ClientOnly>
      <h1
        v-if="fallbackTitle && !barClaimed"
        data-appbar-mobile-title
        class="sm:hidden flex-1 min-w-0 self-center truncate pl-4 pr-3 text-sm font-semibold"
      >{{ fallbackTitle }}</h1>
    </ClientOnly>

    <!-- The page's own header, teleported in. Empty on /app and /app/c/* — there is no page there. -->
    <div
      :id="APP_BAR_SLOT_ID"
      class="min-w-0 flex items-center gap-1.5 overflow-hidden pl-4 pr-3"
      :class="[
        isSidebarCollapsed ? 'sm:pl-1' : 'sm:pl-4',
        // Below sm the fallback title (above) takes the flexible width when it is showing.
        fallbackTitle && !barClaimed ? 'flex-none sm:flex-1' : 'flex-1',
      ]"
    />

    <!-- Phone only: the Activity Center bell. `IconRail` owns it from `sm` up and is hidden below, so
         without this the panel had no trigger on a phone at all (MAR-1350). `mobile` drops the
         `data-activity-trigger` hook the desktop dropdown's outside-click guard keys on: that guard
         does not apply to the full-screen sheet, and a second element carrying the hook would make
         every `[data-activity-trigger]` lookup ambiguous. -->
    <div class="sm:hidden shrink-0 flex items-center self-center">
      <ActivityButton :labelled="false" mobile />
    </div>

    <!-- New chat, mobile only. -->
    <button
      type="button"
      class="sm:hidden shrink-0 self-center flex items-center justify-center h-9 w-9 mr-2 rounded-lg text-foreground hover:bg-muted transition-colors"
      :title="t('webChat.newButton')"
      :aria-label="t('webChat.newButton')"
      @click="startNewChat"
    >
      <Plus class="h-4 w-4" />
    </button>
  </header>
</template>
